import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button.tsx";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal";
import courtBranchService from "@/features/court-detail/api/services/court-branches-service";
import { type UserCreateFormSchema, type UserForm, userCreateFormSchema, userFormSchema } from "@/features/dashboard/types/user.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertTriangle, IconDots, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useUserCreateMutation } from "../api/queries/user-queries";
import type { Role } from "../types/role";
import RoleCreateModal from "./role-create-modal";

const UserCreateForm = ({ data }: { data?: UserForm }) => {
	const navigate = useNavigate();
	const userMutation = useUserCreateMutation();

	const form = useForm<UserCreateFormSchema>({
		resolver: zodResolver(userCreateFormSchema),
		defaultValues: {
			id: data?.id || null,
			first_name: data?.first_name || "",
			middle_name: data?.middle_name || "",
			last_name: data?.last_name || "",
			email: data?.email || "",
			suffix: data?.suffix || "",
			password: data?.password || "",
			password_confirmation: data?.password_confirmation || "",
			roles: data?.roles || [],
			court_branch_id: data?.court_branch_id || "",
			court_branch: data?.court_branch_id || "",
		},
	});
	const modal = useModal();

	const handleRemoveRole = (role: Role) => {
		modal.openModal({
			title: "Remove Role",
			description: "You are revoking this role. Please confirm.",
			type: "confirm",
			variant: "green",
			onConfirm: () => {
				const updatedRoles = form.getValues("roles").filter((r) => r.id !== role.id);
				form.setValue("roles", updatedRoles, { shouldValidate: true });
			},
		});
	};

	const roles = form.watch("roles");
	const courtBranch = form.watch("court_branch");

	const handleAddRole = () => {
		modal.openModal({
			title: "Add Role",
			component: (
				<RoleCreateModal
					handleSubmit={(values) => {
						const currentRoles = form.getValues("roles") || [];
						form.setValue("roles", [...currentRoles, values], { shouldValidate: true });
					}}
				/>
			),
		});
	};

	const handleSubmit = async (values: UserCreateFormSchema) => {
		const response = await userMutation.mutateAsync(values);
		await navigate({
			to: "/users/$userId",
			params: {
				userId: response.id,
			},
		});
	};

	return (
		<Form {...form}>
			<form className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">User Information</span>
							<span className="text-base text-foreground/70">Information that shows on invoices and account when logging in.</span>
						</div>
					</div>
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="first_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>First Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="middle_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Middle Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="last_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Last Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="suffix"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Suffix</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Email Address</FormLabel>
										<FormGroup>
											<Input autoComplete="off" className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 flex">
							<FormField
								control={form.control}
								name="court_branch_id"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Court Branch</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Combobox
												returnFullOption
												onValueChange={(value) => {
													console.log(value);
													form.setValue("court_branch_id", value?.value);
													form.setValue("court_branch", {
														id: value?.value,
														name: value?.label,
														judge: value?.judge,
														category: value?.category,
														address: value?.address,
													});
												}}
												placeholderValue={{
													label: courtBranch?.name ?? "",
													value: courtBranch?.id ?? "",
												}}
												loadOptions={async (value: string) => {
													const response = await courtBranchService.list({
														"filter[search]": value,
													});

													return response.data.map((branch: any) => ({
														label: branch.formatted_name,
														value: branch.id,
														judge: branch.judge,
														category: branch.category,
														address: branch.address,
													}));
												}}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				<div className="flex flex-col w-full gap-6">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">Roles</span>
							<span className="text-base text-foreground/70">Information that shows on invoices and account when logging in.</span>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12 flex flex-col w-full">
							<Button className="max-w-20" onClick={handleAddRole} color="gray" type="button" size="sm">
								<IconPlus size={16} stroke={2} />
								Add Role
							</Button>
							{roles?.length > 0 && (
								<table className="w-full mt-2">
									<thead>
										<tr className="border-b-2 border-slate-200">
											<th className="text-left p-2 text-xs text-gray-800 w-[400px]">Role</th>
											<th className="text-right p-2 text-xs text-gray-800" />
										</tr>
									</thead>
									<tbody>
										{roles?.map((role) => (
											<tr key={role.id} className="border-b border-slate-200 last:border-b-0">
												<td className="text-left p-2 text-sm text-gray-600">
													<div className="flex flex-row items-center w-auto gap-2">
														<div className="flex flex-col w-auto gap-1">
															<span className="leading-3 text-xs font-semibold text-blue-600 hover:text-blue-500">{role.name}</span>
														</div>
													</div>
												</td>
												<td className="text-right p-2 text-xs text-gray-600">
													<div className="flex flex-row justify-end">
														<DropdownMenu modal={false}>
															<DropdownMenuTrigger asChild>
																<Button className="translate-y-[1px]" mode="icon-circle" size="xs" variant="light">
																	<IconDots size={16} stroke={1.5} />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent>
																<DropdownMenuItem onSelect={() => handleRemoveRole(role)} dangerZone>
																	<div className="flex flex-row items-center gap-1">
																		<IconAlertTriangle size={16} stroke={2} />
																		<span className="translate-y-[-1px] leading-1">Revoke Role</span>
																	</div>
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				</div>
				<div className="flex flex-col w-full gap-6">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">Security</span>
							<span className="text-base text-foreground/70">Information that shows on invoices and account when logging in.</span>
						</div>
					</div>
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6 flex flex-col w-full">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Password</FormLabel>
										<FormGroup>
											<Input type="password" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 flex flex-col w-full">
							<FormField
								control={form.control}
								name="password_confirmation"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Confirm Password</FormLabel>
										<FormGroup>
											<Input type="password" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
					<div className="col-span-12 flex">
						<Button type="submit">Save changes</Button>
					</div>
				</div>
			</form>
		</Form>
	);
};

export default UserCreateForm;
